/**
 * Enhanced Withdrawal Screen
 * With bank details form and status tracking
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Icon from '../components/Icon';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../services/authApi';
import { userAPI } from '../services/api';

const WithdrawalScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);
  
  // Bank Details Form
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [upiId, setUpiId] = useState('');
  const [usdWalletAddress, setUsdWalletAddress] = useState('');
  const [withdrawals, setWithdrawals] = useState([]);

  const withdrawalMethods = [
    {
      id: '1',
      name: 'Bank Transfer',
      apiMethod: 'BANK',
      icon: 'business',
      color: '#4A90E2',
      fee: '$0',
      minWithdraw: '$100',
      processingTime: '3-5 days',
      requiresBankDetails: true,
    },
    {
      id: '2',
      name: 'UPI',
      apiMethod: 'UPI',
      icon: 'phone-portrait',
      color: '#00E676',
      fee: '$0',
      minWithdraw: '$10',
      processingTime: 'Instant',
      requiresUPI: true,
    },
    {
      id: '3',
      name: 'USDT (TRC20)',
      apiMethod: 'USDT',
      icon: 'logo-bitcoin',
      color: '#00E676',
      fee: 'Network fee',
      minWithdraw: '$50',
      processingTime: '1-6 hours',
      requiresWalletAddress: true,
    },
  ];

  useEffect(() => {
    fetchUserProfile();
    fetchWithdrawals();
  }, []);

  // Auto-refresh when screen is focused
  useFocusEffect(
    React.useCallback(() => {
      console.log('Withdrawal screen focused - fetching user profile');
      fetchUserProfile();
      fetchWithdrawals();
    }, [])
  );

  const fetchWithdrawals = async () => {
    try {
      console.log('Fetching withdrawal history...');
      const withdrawalsData = await userAPI.getWithdrawals();
      console.log('Withdrawals data:', withdrawalsData);
      setWithdrawals(withdrawalsData || []);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      if (error.response?.status === 404) {
        console.log('Withdrawals endpoint not found');
        setWithdrawals([]);
      }
    }
  };

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching user profile for withdrawal screen...');
      
      const profile = await authAPI.getProfile();
      console.log('User profile for withdrawal:', profile);
      
      setUserProfile(profile);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Set default profile on error instead of redirecting to login
      setUserProfile({ balance: 0, name: 'User', email: '' });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate wallet balance - show total balance as available balance
  const walletBalance = {
    total: userProfile?.balance || 0,
    available: userProfile?.balance || 0, // Show full balance as available
    locked: ((userProfile?.balance || 0) * 0.11),
  };

  const quickAmounts = ['100', '250', '500', '1000', 'All'];

  // Update quick amount logic to use actual available balance
  const getQuickAmountValue = (quickAmount) => {
    if (quickAmount === 'All') {
      return walletBalance.available.toFixed(0);
    }
    return quickAmount;
  };

  // Validate withdrawal form
  const validateWithdrawal = () => {
    const withdrawAmount = parseFloat(amount);
    
    if (!selectedMethod) {
      Alert.alert('Error', 'Please select a withdrawal method');
      return false;
    }
    
    if (!amount || withdrawAmount <= 0) {
      Alert.alert('Error', 'Please enter a valid withdrawal amount');
      return false;
    }
    
    if (withdrawAmount > walletBalance.available) {
      Alert.alert('Error', `Insufficient balance. Available: $${walletBalance.available.toFixed(2)}`);
      return false;
    }
    
    const minAmount = parseFloat(selectedMethod.minWithdraw.replace('$', ''));
    if (withdrawAmount < minAmount) {
      Alert.alert('Error', `Minimum withdrawal amount is ${selectedMethod.minWithdraw}`);
      return false;
    }
    
    // Validate bank details if bank transfer is selected
    if (selectedMethod.requiresBankDetails) {
      if (!bankName || !accountNumber || !accountHolderName || !ifscCode) {
        Alert.alert('Error', 'Please fill all bank details for bank transfer');
        return false;
      }
    }
    
    // Validate UPI if UPI is selected
    if (selectedMethod.requiresUPI) {
      if (!upiId) {
        Alert.alert('Error', 'Please enter UPI ID');
        return false;
      }
    }
    
    // Validate wallet address if USDT is selected
    if (selectedMethod.requiresWalletAddress) {
      if (!usdWalletAddress || usdWalletAddress.length < 10 || usdWalletAddress.length > 100) {
        Alert.alert('Error', 'Please enter a valid USD wallet address (10-100 characters)');
        return false;
      }
    }
    
    return true;
  };

  // Handle withdrawal submission
  const handleWithdrawal = async () => {
    if (!validateWithdrawal()) return;
    
    try {
      setIsSubmitting(true);
      
      // Prepare withdrawal data based on method
      const withdrawalData = {
        amount: parseFloat(amount),
        method: selectedMethod.apiMethod,
      };
      
      // Add method-specific fields
      if (selectedMethod.requiresBankDetails) {
        withdrawalData.bankName = bankName;
        withdrawalData.accountNumber = accountNumber;
        withdrawalData.accountHolderName = accountHolderName;
        withdrawalData.ifscCode = ifscCode;
      } else if (selectedMethod.requiresUPI) {
        withdrawalData.upiId = upiId;
      } else if (selectedMethod.requiresWalletAddress) {
        withdrawalData.usdtAddress = usdWalletAddress;
      }
      
      console.log('🚀 Submitting withdrawal request:', withdrawalData);
      
      // Call the withdrawal API
      const response = await userAPI.createWithdrawal(withdrawalData);
      console.log('✅ Withdrawal API response:', response);
      console.log('✅ Response data:', response?.data);
      
      // Show custom success modal
      const modalData = {
        amount: parseFloat(amount).toFixed(2),
        withdrawalId: response?.data?.withdrawalId || response?.data?._id || response?.withdrawalId || response?._id || 'Pending',
        paymentMethod: selectedMethod.name,
        processingTime: selectedMethod.processingTime
      };
      console.log('🔔 Setting modal data:', modalData);
      setSuccessModalData(modalData);
      console.log('🔔 Setting modal visible to true...');
      setShowSuccessModal(true);
      console.log('✅ Modal state updated, should be visible now');
      
    } catch (error) {
      console.error('Withdrawal error:', error);
      let errorMessage = 'Failed to submit withdrawal request. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid withdrawal request. Please check your details.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Withdrawal not allowed. Please verify your account.';
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPaymentMethod = (method) => (
    <TouchableOpacity
      key={method.id}
      style={[
        styles.methodCard,
        { backgroundColor: colors.cardBackground, borderColor: colors.border },
        selectedMethod?.id === method.id && { borderColor: colors.primary, borderWidth: 2 },
      ]}
      onPress={() => setSelectedMethod(method)}>
      <View style={styles.methodHeader}>
        <View style={[styles.methodIcon, { backgroundColor: `${method.color}20` }]}>
          <Icon name={method.icon} size={28} color={method.color} />
        </View>
        <View style={styles.methodInfo}>
          <Text style={[styles.methodName, { color: colors.text }]}>{method.name}</Text>
          <View style={styles.methodBadges}>
            <View style={[styles.feeBadge, { backgroundColor: `${colors.primary}26` }]}>
              {/* <Text style={[styles.feeText, { color: colors.primary }]}>Fee: {method.fee}</Text> */}
            </View>
            <View style={[styles.timeBadge, { backgroundColor: `${colors.textSecondary}26` }]}>
              <Icon name="time-outline" size={10} color={colors.textSecondary} />
              <Text style={[styles.timeText, { color: colors.textSecondary }]}> {method.processingTime}</Text>
            </View>
          </View>
        </View>
        {selectedMethod?.id === method.id && (
          <Icon name="checkmark-circle" size={24} color={method.color} />
        )}
      </View>
      <View style={[styles.methodDetails, { borderTopColor: colors.border }]}>
        <Text style={[styles.detailText, { color: colors.textSecondary }]}>Min: {method.minWithdraw}</Text>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />
      
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.headerBackground, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.primary }]}>Withdraw Funds</Text>
        <TouchableOpacity style={styles.helpButton}>
          <Icon name="help-circle-outline" size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>

      <ScrollView showsVerticalScrollIndicator={false}>
        {isLoading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={{ color: colors.textSecondary, marginTop: 10 }}>Loading wallet...</Text>
          </View>
        ) : (
          <>
            {/* Balance Card */}
            <View style={[styles.balanceCard, { backgroundColor: `${colors.primary}1A`, borderColor: `${colors.primary}33` }]}>
              <View style={[styles.balanceHeader, { borderBottomColor: `${colors.primary}33` }]}>
                <Icon name="wallet" size={32} color={colors.primary} />
                <View style={styles.balanceInfo}>
                  <Text style={[styles.balanceLabel, { color: colors.textSecondary }]}>Available Balance</Text>
                  <Text style={[styles.balanceValue, { color: colors.primary }]}>${walletBalance.total.toFixed(2)}</Text>
                </View>
              </View>
            </View>

            {/* Verification Notice */}
            <View style={[styles.verificationNotice, { backgroundColor: `${colors.success}1A`, borderColor: `${colors.success}33` }]}>
              <Icon name="shield-checkmark" size={20} color={colors.success} />
              <View style={styles.verificationTextContainer}>
                <Text style={[styles.verificationTitle, { color: colors.success }]}>Account Verified</Text>
                <Text style={[styles.verificationSubtitle, { color: colors.textSecondary }]}>Withdrawals enabled</Text>
              </View>
            </View>

            {/* Amount Input */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Withdrawal Amount</Text>
              <View style={[styles.amountInputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                <Text style={[styles.currencySymbol, { color: colors.primary }]}>$</Text>
                <TextInput
                  style={[styles.amountInput, { color: colors.text }]}
                  placeholder="0.00"
                  placeholderTextColor={colors.textTertiary}
                  keyboardType="numeric"
                  value={amount}
                  onChangeText={setAmount}
                />
              </View>

              {/* Quick Amount Buttons */}
              <View style={styles.quickAmountsContainer}>
                {quickAmounts.map((quickAmount) => (
                  <TouchableOpacity
                    key={quickAmount}
                    style={[
                      styles.quickAmountButton,
                      { backgroundColor: colors.cardBackground, borderColor: colors.border },
                      amount === getQuickAmountValue(quickAmount) && { backgroundColor: `${colors.primary}33`, borderColor: colors.primary },
                    ]}
                    onPress={() => setAmount(getQuickAmountValue(quickAmount))}>
                    <Text
                      style={[
                        styles.quickAmountText,
                        { color: colors.textSecondary },
                        amount === getQuickAmountValue(quickAmount) && { color: colors.primary },
                      ]}>
                      {quickAmount === 'All' ? 'All' : `$${quickAmount}`}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Withdrawal Methods */}
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Select Withdrawal Method</Text>
              {withdrawalMethods.map(renderPaymentMethod)}
            </View>

            {/* Bank Details Form - Show when bank transfer is selected */}
            {selectedMethod?.requiresBankDetails && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Bank Details</Text>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="business" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="Bank Name"
                    placeholderTextColor={colors.textTertiary}
                    value={bankName}
                    onChangeText={setBankName}
                  />
                </View>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="card" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="Account Number"
                    placeholderTextColor={colors.textTertiary}
                    keyboardType="numeric"
                    value={accountNumber}
                    onChangeText={setAccountNumber}
                  />
                </View>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="person" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="Account Holder Name"
                    placeholderTextColor={colors.textTertiary}
                    value={accountHolderName}
                    onChangeText={setAccountHolderName}
                  />
                </View>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="code" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="IFSC Code"
                    placeholderTextColor={colors.textTertiary}
                    value={ifscCode}
                    onChangeText={setIfscCode}
                    autoCapitalize="characters"
                  />
                </View>
              </View>
            )}

            {/* UPI Details Form - Show when UPI is selected */}
            {selectedMethod?.requiresUPI && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>UPI Details</Text>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="phone-portrait" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="UPI ID (e.g., user@paytm)"
                    placeholderTextColor={colors.textTertiary}
                    value={upiId}
                    onChangeText={setUpiId}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                </View>
              </View>
            )}

            {/* USD Wallet Address Form - Show when USDT is selected */}
            {selectedMethod?.requiresWalletAddress && (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { color: colors.text }]}>Wallet Details</Text>
                
                <View style={[styles.inputContainer, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
                  <Icon name="wallet" size={20} color={colors.textSecondary} />
                  <TextInput
                    style={[styles.textInput, { color: colors.text }]}
                    placeholder="USDT Wallet Address (TRC20)"
                    placeholderTextColor={colors.textTertiary}
                    value={usdWalletAddress}
                    onChangeText={setUsdWalletAddress}
                    autoCapitalize="none"
                    maxLength={100}
                  />
                </View>
                <Text style={[styles.inputHelper, { color: colors.textSecondary }]}>
                  Enter your USDT TRC20 wallet address (10-100 characters)
                </Text>
              </View>
            )}

            {/* Warning Notice */}
            <View style={[styles.warningNotice, { backgroundColor: `${colors.warning}1A`, borderColor: `${colors.warning}33` }]}>
              <Icon name="warning" size={20} color={colors.warning} />
              <Text style={[styles.warningText, { color: colors.textSecondary }]}>
                Withdrawals are processed within the stated time. Ensure your payment details are correct to avoid delays.
              </Text>
            </View>

            {/* Withdrawal Button */}
            <TouchableOpacity
              style={[
                styles.withdrawButton,
                { backgroundColor: colors.primary },
                (!selectedMethod || !amount || isSubmitting) && { backgroundColor: `${colors.textTertiary}4D`, shadowOpacity: 0 },
              ]}
              disabled={!selectedMethod || !amount || isSubmitting}
              onPress={handleWithdrawal}>
              {isSubmitting ? (
                <ActivityIndicator size="small" color={colors.text} />
              ) : (
                <Icon name="cash-outline" size={24} color={colors.text} />
              )}
              <Text style={[styles.withdrawButtonText, { color: colors.text }]}>
                {isSubmitting ? 'Processing...' : `Request Withdrawal $${amount || '0.00'}`}
              </Text>
            </TouchableOpacity>

            <View style={{ height: 100 }} />
          </>
        )}
      </ScrollView>

      {/* Custom Success Modal */}
      <Modal
        visible={showSuccessModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowSuccessModal(false);
          // Reset form
          setAmount('');
          setSelectedMethod(null);
          setBankName('');
          setAccountNumber('');
          setAccountHolderName('');
          setIfscCode('');
          setUpiId('');
          setUsdWalletAddress('');
          fetchUserProfile();
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, { backgroundColor: colors.cardBackground }]}>
            <View style={styles.modalHeader}>
              <View style={[styles.successIcon, { backgroundColor: '#4CAF5020' }]}>
                <Icon name="checkmark-circle" size={50} color="#4CAF50" />
              </View>
              <Text style={[styles.modalTitle, { color: colors.text }]}>Successfully Requested!</Text>
            </View>
            
            <View style={styles.modalContent}>
              <Text style={[styles.modalText, { color: colors.textSecondary }]}>
                Your withdrawal request has been submitted for admin approval.
              </Text>
              
              {successModalData && (
                <View style={styles.modalDetails}>
                  <View style={styles.modalDetailRow}>
                    <Text style={[styles.modalDetailLabel, { color: colors.textSecondary }]}>Amount:</Text>
                    <Text style={[styles.modalDetailValue, { color: colors.text }]}>${successModalData.amount}</Text>
                  </View>
                  <View style={styles.modalDetailRow}>
                    <Text style={[styles.modalDetailLabel, { color: colors.textSecondary }]}>Withdrawal ID:</Text>
                    <Text style={[styles.modalDetailValue, { color: colors.text }]}>{successModalData.withdrawalId}</Text>
                  </View>
                  <View style={styles.modalDetailRow}>
                    <Text style={[styles.modalDetailLabel, { color: colors.textSecondary }]}>Payment Method:</Text>
                    <Text style={[styles.modalDetailValue, { color: colors.text }]}>{successModalData.paymentMethod}</Text>
                  </View>
                </View>
              )}
              
              <Text style={[styles.modalSubtext, { color: colors.textSecondary }]}>
                Admin will review your withdrawal request and process the payment. Amount will be transferred to your account after approval.
              </Text>
              
              <Text style={[styles.modalSubtext, { color: colors.textSecondary }]}>
                You can track the status in your submissions.
              </Text>
            </View>
            
            <TouchableOpacity
              style={[styles.modalButton, { backgroundColor: colors.primary }]}
              onPress={() => {
                setShowSuccessModal(false);
                // Reset form
                setAmount('');
                setSelectedMethod(null);
                setBankName('');
                setAccountNumber('');
                setAccountHolderName('');
                setIfscCode('');
                setUpiId('');
                setUsdWalletAddress('');
                fetchUserProfile();
              }}
            >
              <Text style={styles.modalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 50,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  helpButton: {
    padding: 4,
  },
  balanceCard: {
    marginHorizontal: 16,
    marginTop: 20,
    marginBottom: 12,
    padding: 20,
    borderRadius: 20,
    borderWidth: 1,
  },
  balanceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  balanceInfo: {
    flex: 1,
  },
  balanceLabel: {
    fontSize: 13,
    marginBottom: 4,
  },
  balanceValue: {
    fontSize: 32,
    fontWeight: '700',
  },
  balanceFooter: {
    flexDirection: 'row',
    gap: 20,
  },
  balanceItem: {
    flex: 1,
  },
  balanceItemLabel: {
    fontSize: 11,
    marginBottom: 4,
  },
  balanceItemValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  verificationNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginHorizontal: 16,
    marginBottom: 20,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  verificationTextContainer: {
    flex: 1,
  },
  verificationTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  verificationSubtitle: {
    fontSize: 11,
  },
  section: {
    paddingHorizontal: 16,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
  },
  amountInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderWidth: 1,
  },
  currencySymbol: {
    fontSize: 24,
    fontWeight: '700',
    marginRight: 8,
  },
  amountInput: {
    flex: 1,
    fontSize: 32,
    fontWeight: '700',
  },
  quickAmountsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 16,
  },
  quickAmountButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
  },
  quickAmountText: {
    fontSize: 14,
    fontWeight: '600',
  },
  methodCard: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  methodHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  methodIcon: {
    width: 56,
    height: 56,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  methodInfo: {
    flex: 1,
  },
  methodName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 6,
  },
  methodBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  feeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  feeText: {
    fontSize: 10,
    fontWeight: '600',
  },
  timeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  timeText: {
    fontSize: 10,
    fontWeight: '500',
  },
  methodDetails: {
    paddingTop: 12,
    borderTopWidth: 1,
  },
  detailText: {
    fontSize: 12,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 12,
    borderWidth: 1,
    gap: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
  },
  warningNotice: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginHorizontal: 16,
    marginBottom: 20,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 18,
  },
  withdrawButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
  },
  withdrawButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  inputHelper: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 16,
  },
  // Custom Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
  },
  modalContent: {
    marginBottom: 24,
  },
  modalText: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 16,
  },
  modalDetails: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  modalDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  modalDetailLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  modalDetailValue: {
    fontSize: 13,
    fontWeight: '600',
  },
  modalSubtext: {
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    marginTop: 8,
  },
  modalButton: {
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default WithdrawalScreen;
